import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LogOut, Users, BarChart3, Calendar, Settings, UserCog,
  RefreshCw, Building2, Mail, Activity, ShoppingBag, Package,
  CreditCard, Sparkles, ChevronRight, ArrowRight, CalendarCheck,
  UtensilsCrossed, ShieldCheck, Hand, QrCode,
} from "lucide-react";
import { Brand } from "../components/Brand";
import { useAuth } from "../lib/auth";
import { reservasApi } from "../lib/api";
import { esMaestro, puedeVerEncuestaQr, accesoEfectivo } from "../lib/permisos";

type SectionKey = "operacion" | "catalogo" | "sistema";

interface ModuleCard {
  icon: typeof Users;
  name: string;
  desc: string;
  to: string;
  accent: string;
  section: SectionKey;
  badge?: number;
  show: boolean;
}

interface SectionMeta {
  key: SectionKey;
  title: string;
  subtitle: string;
  icon: typeof Users;
  accent: string;
}

const SECCIONES: SectionMeta[] = [
  {
    key: "operacion",
    title: "Operación diaria",
    subtitle: "Reservas, delivery y gestión de clientes",
    icon: Calendar,
    accent: "#F59E0B",
  },
  {
    key: "catalogo",
    title: "Catálogo & Cobros",
    subtitle: "Artículos, menú digital y métodos de pago",
    icon: UtensilsCrossed,
    accent: "#F97316",
  },
  {
    key: "sistema",
    title: "Administración del sistema",
    subtitle: "Usuarios, sucursales, sincronización y configuración",
    icon: ShieldCheck,
    accent: "#6366F1",
  },
];

export function DashboardPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [pendientes, setPendientes] = useState<number | null>(null);

  useEffect(() => {
    reservasApi.countPendientes()
      .then((r) => setPendientes(r.count))
      .catch(() => setPendientes(null));
    const interval = setInterval(() => {
      reservasApi.countPendientes()
        .then((r) => setPendientes(r.count))
        .catch(() => { });
    }, 30_000);
    return () => clearInterval(interval);
  }, []);

  function handleLogout() {
    logout();
    navigate("/login");
  }

  const firstName = user?.fullName?.split(" ")?.[0] ?? "Admin";

  const modules: ModuleCard[] = [
    // ── OPERACIÓN ──
    {
      icon: CalendarCheck, name: "Reservas",
      desc: "Confirmar, gestionar y seguimiento",
      to: "/reservas", accent: "#F59E0B",
      section: "operacion", show: true,
      badge: pendientes ?? undefined,
    },
    {
      icon: ShoppingBag, name: "Delivery",
      desc: "Tablero de pedidos en tiempo real",
      to: "/delivery", accent: "#F97316",
      section: "operacion", show: true,
    },
    {
      icon: Users, name: "Cliente 360°",
      desc: "Historial, hábitos y estadísticas",
      to: "/cliente360", accent: "#3B82F6",
      section: "operacion", show: true,
    },
    {
      icon: QrCode, name: "Encuestas (QR)",
      desc: "Mostrale el QR al cliente para que la responda",
      to: "/encuestas-qr", accent: "#EAB308",
      section: "operacion",
      show: puedeVerEncuestaQr(user),
    },

    // ── CATÁLOGO & COBROS ──
    {
      icon: Package, name: "Configurar artículos",
      desc: "Fotos, precios, agotados, destacados",
      to: "/articulos-config", accent: "#8B5CF6",
      section: "catalogo", show: true,
    },
    {
      icon: CreditCard, name: "Pasarelas de pago",
      desc: "PayPal, Pago Móvil, Zelle, Stripe...",
      to: "/pasarelas-pago", accent: "#06B6D4",
      section: "catalogo", show: true,
    },

    // ── ADMINISTRACIÓN DEL SISTEMA ──
    {
      icon: UserCog, name: "Usuarios",
      desc: "Equipo, roles y permisos",
      to: "/usuarios", accent: "#6366F1",
      section: "sistema",
      show: esMaestro(user),
    },
    {
      icon: Building2, name: "Sucursales",
      desc: "Logo, contacto, zonas y horarios",
      to: "/sucursales", accent: "#8B5CF6",
      section: "sistema",
      show: esMaestro(user),
    },
    {
      icon: Mail, name: "Correo (SMTP)",
      desc: "Servidor de salida y configuración",
      to: "/smtp-config", accent: "#0EA5E9",
      section: "sistema",
      show: esMaestro(user),
    },
    {
      icon: RefreshCw, name: "Sincronización",
      desc: "Estado de syncs ICG → CRM y logs",
      to: "/sincronizacion", accent: "#10B981",
      section: "sistema",
      show: esMaestro(user),
    },
    {
      icon: Activity, name: "Diagnóstico del sistema",
      desc: "Health checks y queries de admin",
      to: "/admin/diagnosticos", accent: "#EF4444",
      section: "sistema",
      show: user?.superAdmin === true,
    },
  ];

  const visibles = modules.filter((m) => accesoEfectivo(user, m.to, m.show));
  const porSeccion = (key: SectionKey) => visibles.filter((m) => m.section === key);
  const reservasUrgentes = pendientes !== null && pendientes > 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-ink-50 via-ink-50 to-white">
      {/* HEADER */}
      <header className="border-b border-ink-200/60 bg-white/80 backdrop-blur sticky top-0 z-30">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3">
          <Brand size="sm" />
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-ink-800">{user?.fullName}</p>
              <p className="text-xs text-ink-500 inline-flex items-center gap-1.5 capitalize">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
                {user?.role}
                {user?.superAdmin && (
                  <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] bg-amber-100 text-amber-700 font-bold uppercase">
                    Super
                  </span>
                )}
              </p>
            </div>
            <button onClick={handleLogout}
              className="size-9 rounded-lg bg-white border border-ink-200 hover:bg-red-50 hover:border-red-200 hover:text-red-600 transition-colors flex items-center justify-center"
              aria-label="Cerrar sesión" title="Cerrar sesión">
              <LogOut className="size-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10 animate-fade-in">

        {/* ════ HERO ════ */}
        <div className="mb-8">
          <p className="text-xs font-medium uppercase tracking-widest text-ink-400 mb-1">
            Panel de administración
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-ink-900 leading-tight">
            Hola, {firstName}{" "}
            <Hand className="inline-block align-middle text-amber-500 animate-wiggle" size={36} strokeWidth={2} aria-hidden />
          </h1>
          <p className="mt-2 text-ink-500 max-w-lg">
            Bienvenido al CRM RedesIP. Gestiona todo el negocio desde aquí.
          </p>
        </div>

        {/* ════ BANNER ALERTA RESERVAS (si hay) ════ */}
        {reservasUrgentes && (
          <Link to="/reservas?tab=pendientes"
            className="block mb-8 rounded-2xl bg-gradient-to-r from-amber-50 to-amber-100/50 border border-amber-200 p-4 hover:from-amber-100 hover:to-amber-200/40 transition-colors group">
            <div className="flex items-center gap-4">
              <div className="size-12 rounded-xl bg-amber-200 text-amber-700 flex items-center justify-center shrink-0 shadow-sm">
                <CalendarCheck className="size-6" />
              </div>
              <div className="flex-1">
                <div className="font-bold text-amber-900 text-base">
                  {pendientes} reserva{pendientes !== 1 ? "s" : ""} pendiente{pendientes !== 1 ? "s" : ""} de confirmar
                </div>
                <p className="text-sm text-amber-700 mt-0.5">
                  Confírmalas para liberarlas en el calendario
                </p>
              </div>
              <ArrowRight className="size-5 text-amber-600 shrink-0 group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>
        )}

        {/* ════ SECCIONES ════ */}
        {SECCIONES.map((sec) => {
          const items = porSeccion(sec.key);
          if (items.length === 0) return null;
          return <Section key={sec.key} meta={sec} items={items} />;
        })}

        {/* Footer info */}
        <div className="mt-12 text-center text-xs text-ink-400">
          CRM RedesIP · {visibles.length} módulos activos
        </div>
      </main>

      <footer className="border-t border-ink-200/60 bg-white/60 py-5">
        <div className="mx-auto max-w-7xl px-6 text-center text-xs text-ink-400">
          Powered by <span className="text-brand-600 font-medium">redesip.org</span>
        </div>
      </footer>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// Sección
// ════════════════════════════════════════════════════════════
function Section({ meta, items }: { meta: SectionMeta; items: ModuleCard[] }) {
  return (
    <section className="mb-10">
      <div className="flex items-center gap-3 mb-4">
        <div className="size-10 rounded-xl flex items-center justify-center shrink-0 shadow-sm"
          style={{ background: `${meta.accent}18`, color: meta.accent }}>
          <meta.icon className="size-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-display font-bold text-xl text-ink-900 leading-tight">
            {meta.title}
          </h2>
          <p className="text-xs text-ink-500 mt-0.5">{meta.subtitle}</p>
        </div>
        <span className="text-xs text-ink-400 hidden sm:inline">
          {items.length} {items.length === 1 ? "módulo" : "módulos"}
        </span>
      </div>

      <div className="h-px bg-gradient-to-r from-ink-200 via-ink-100 to-transparent mb-4" />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {items.map((m) => <Card key={m.name} m={m} />)}
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════
// Card
// ════════════════════════════════════════════════════════════
function Card({ m }: { m: ModuleCard }) {
  return (
    <Link to={m.to}
      className="group relative bg-white rounded-2xl p-4 border border-ink-200/60 hover:border-transparent hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 overflow-hidden">

      <div className="absolute -inset-px opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none rounded-2xl blur-sm"
        style={{ background: `radial-gradient(at top left, ${m.accent}30, transparent 70%)` }} />

      <div className="relative">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="size-11 rounded-xl flex items-center justify-center shadow-sm transition-transform group-hover:scale-110"
            style={{ background: `${m.accent}18`, color: m.accent }}>
            <m.icon className="size-5" />
          </div>
          {m.badge !== undefined && m.badge > 0 && (
            <span className="text-xs px-2.5 py-1 rounded-full font-bold text-white shadow-sm animate-pulse"
              style={{ background: m.accent }}>
              {m.badge}
            </span>
          )}
        </div>

        <h3 className="font-bold mb-1 leading-tight text-ink-900">{m.name}</h3>
        <p className="text-xs text-ink-500 leading-relaxed line-clamp-2 min-h-[2.4em]">
          {m.desc}
        </p>

        <div className="flex items-center gap-1 mt-3 text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ color: m.accent }}>
          Abrir <ArrowRight className="size-3 group-hover:translate-x-0.5 transition-transform" />
        </div>
      </div>
    </Link>
  );
}
